import { CHECKOUT_SDK_ERROR_CODES } from '@ekhodealer/ekho-common';

import {
  CheckoutSdkError,
  errorCodeFromBodyOrStatus,
  type SdkErrorBody,
} from '../../utils/sdk-error';

const FETCH_TIMEOUT_MS = 15000;

/**
 * Centralized API client for the SDK. Uses fetch (no axios — keeps the
 * bundle small) with a timeout, typed error handling, and a configurable
 * base URL resolved from the SDK config at init time.
 */
export class SdkApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private buildUrl(path: string): string {
    const separator = this.baseUrl.endsWith('/') ? '' : '/';
    return `${this.baseUrl}${separator}${path}`;
  }

  private async fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      return await fetch(url, { ...init, signal: controller.signal });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new CheckoutSdkError('Request timed out', CHECKOUT_SDK_ERROR_CODES.NETWORK_ERROR);
      }
      throw new CheckoutSdkError('Network request failed', CHECKOUT_SDK_ERROR_CODES.NETWORK_ERROR, {
        cause: err instanceof Error ? err.message : String(err),
      });
    } finally {
      clearTimeout(timer);
    }
  }

  private async handleResponse<T>(res: Response): Promise<T> {
    if (res.ok) return (await res.json()) as T;

    // Prefer the server's typed `{ code, message, details? }` body so the
    // promised `switch (err.code)` API in the docs works regardless of HTTP
    // status. Fall back to deriving from status when the body isn't JSON or
    // doesn't carry a valid code (old server, proxy interception, etc.).
    let body: SdkErrorBody | undefined;
    try {
      body = (await res.json()) as SdkErrorBody;
    } catch {
      // Response body not JSON — ignore.
    }
    const code = errorCodeFromBodyOrStatus(body, res.status);
    const message = body?.message ?? `Request failed with status ${res.status}`;
    throw new CheckoutSdkError(message, code, body?.details);
  }

  async get<T>(path: string, params?: URLSearchParams): Promise<T> {
    const base = this.buildUrl(path);
    const url = params ? `${base}?${params.toString()}` : base;
    const res = await this.fetchWithTimeout(url, { method: 'GET', credentials: 'omit' });
    return this.handleResponse<T>(res);
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const url = this.buildUrl(path);
    const res = await this.fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'omit',
    });
    return this.handleResponse<T>(res);
  }
}
